import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, Popper } from '@material-ui/core';
import { EditorState } from 'draft-js';
import _ from 'lodash';
import { AutoCompleteSelect, RichText } from '../../../../../components';
import Dialog from '../../../../../components/Dialog';
import { AddIcon, DropDownIcon, DropUpIcon } from '../../../../../components/Icon';
import LongRemark from '../../../../../components/LongRemark';
import { editorStateInsertText, initColor } from '../../../../../utils/newBedStatusUtil';
import AutoCompleteInput from '../../../../../components/AutoCompleteInput';
import {
  BED_STATUS_REMARK_COLOR,
  NEW_BED_STATUS_CUBICLE_ITEM_LIST,
  NEW_BED_STATUS_LENGTH,
} from '../../../../../comm/constant';
import useUpdateEffect from '../../../../../utils/useUpdateEffect';
import { Checkbox } from '../../../../DutyAssign/components/Attendance/Setting';
import { initValue } from '../../../../../utils';
import CubicleRemarksPopup from '../../../../BedStatus/newComponents/Popups/CubicleRemarksPopup';
import { getEADBeds } from '../../../../../utils/admissionUtil';
import StyleButton from '../../../../../components/StyleButton';
import { AED_CUBICLE_COLOR_LIST } from '../../LocCubicleDialog';

const INIT_DATA = {
  name: '',
  type: '',
  status: '',
  gender: '',
  color: '',
  remarks: '',
  remarkColor: 'White',
  bedArrangement: { column: '1', row: '1' },
  borderLine: 'hide',
  header: 'show',
};

const DIALOG_TYPE = {
  CUBICLE: 'Cubicle',
  NURSE_STATION: 'Nurse Station/Misc.',
  MEMO: 'Memo',
};

const getDialogType = (cubicleData) => {
  if (cubicleData?.type === DIALOG_TYPE.NURSE_STATION || cubicleData?.type === DIALOG_TYPE.MEMO) {
    return cubicleData.type;
  }

  return DIALOG_TYPE.CUBICLE;
};

const getInitialForm = (cubicleData) => ({
  ...INIT_DATA,
  ...(cubicleData ?? {}),
  bedArrangement: {
    ...INIT_DATA.bedArrangement,
    ...(cubicleData?.bedArrangement ?? {}),
  },
});

const getSubmitBeds = (row, column, beds = []) => {
  return _.map(getEADBeds(row, column), (item) => {
    const oldBed = _.find(beds, (bedItem) => bedItem.row === item.row && bedItem.column === item.column);

    return oldBed ?? item;
  });
};

const getEditorState = (editorState) => {
  return editorState?.getCurrentContent ? editorState : EditorState.createEmpty();
};

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  star: {
    color: '#ff0000',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  remarkInput: {
    width: '100%',
    height: '12.875rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    alignItems: 'flex-start',
    '& .MuiInputBase-inputMultiline': {
      height: '100% !important',
      overflow: 'auto !important',
    },
  },
  titleRemark: {
    color: '#c0c6cb',
    marginLeft: '0.2rem',
  },
  addRemarkBtn: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#538fff',
  },
  addIcon: {
    fontSize: '1.2rem',
    marginRight: '0.45rem',
  },
  border: {
    width: '0.063rem',
    height: '1.188rem',
    backgroundColor: '#e4e7eb',
  },
  palette: ({ form }) => ({
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    marginRight: '0.75rem',
    backgroundColor: initValue(BED_STATUS_REMARK_COLOR[form.remarkColor]?.bgColor, 'fff'),
  }),
  dropDownIcon: {
    color: '#538fff',
    fontSize: '0.75rem',
  },
  remarkColorPopup: {
    zIndex: '9999',
  },
  colourItem: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    width: '100%',
    height: '2.875rem',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '1.563rem',
    '&:hover': {
      backgroundColor: '#f5faff',
    },
  },
  bedArrangement: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#538fff',
  },
  content: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
}));

const LandingUpdateCubicleDialog = ({
  showPopup,
  cubicleId = null,
  cubicleData = null,
  handleClose,
  handleSubmit,
  viewType = '',
}) => {
  const [remarkAnchorEl, setRemarkAnchorEl] = useState(null);
  const [remarkPopupAnchorEl, setRemarkPopupAnchorEl] = useState(null);
  const [form, setForm] = useState(() => getInitialForm(cubicleData));
  const [type, setType] = useState(() => getDialogType(cubicleData));
  const classes = useStyles({ form });

  const NurseStation = useMemo(() => {
    return type === DIALOG_TYPE.NURSE_STATION;
  }, [type]);

  const Memo = useMemo(() => {
    return type === DIALOG_TYPE.MEMO;
  }, [type]);
  // AED View 的 landing popup 也需要显示额外的 Red/Orange。
  const cubicleColorList = viewType === 'AED' ? AED_CUBICLE_COLOR_LIST : NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor;

  useEffect(() => {
    if (showPopup) {
      setForm(getInitialForm(cubicleData));
      setType(getDialogType(cubicleData));
    }
  }, [showPopup, cubicleData]);

  const updateForm = useCallback(
    (type) => (e) => {
      switch (type) {
        case 'name':
          setForm((o) => ({ ...o, [type]: e.target.value }));
          break;
        case 'remarks':
          setForm((o) => ({ ...o, [type]: Memo ? e : e.target.value }));
          break;
        case 'type':
        case 'status':
        case 'gender':
        case 'color':
          setForm((o) => ({ ...o, [type]: e }));
          break;
        case 'remarkColor':
          return () => {
            setRemarkAnchorEl(null);
            setForm((o) => ({ ...o, [type]: e.value }));
          };
        default:
          break;
      }
    },
    [Memo]
  );

  const handleRemarkOpen = useCallback(
    (event) => {
      setRemarkAnchorEl(remarkAnchorEl ? null : event.currentTarget);
    },
    [remarkAnchorEl]
  );

  const handleConfirm = useCallback(() => {
    let params = {
      id: cubicleId,
      data: {
        ...form,
        type: type === DIALOG_TYPE.CUBICLE ? form.type : type,
        beds: getSubmitBeds(form.bedArrangement.row, form.bedArrangement.column, cubicleData?.beds),
      },
    };
    handleSubmit(params);
    handleClose();
  }, [handleSubmit, form, type, cubicleId, cubicleData?.beds]);

  // 选择性别的时候，自动切换默认颜色
  useUpdateEffect(() => {
    setForm((o) => ({
      ...o,
      color: initColor(o.gender),
    }));
  }, [form.gender]);

  // remark popup 开关
  const handleRemarkPopup = useCallback(
    (event) => {
      setRemarkPopupAnchorEl(remarkPopupAnchorEl ? null : event.currentTarget);
    },
    [remarkPopupAnchorEl]
  );

  // 关闭remark popup
  const handleCloseRemarkPopup = useCallback(() => {
    setRemarkPopupAnchorEl(null);
  }, []);

  // 选择remark
  const handleSelectRemark = useCallback(
    (remark, color) => () => {
      setForm((o) => ({
        ...o,
        remarks: Memo
          ? editorStateInsertText(getEditorState(o.remarks), remark)
          : `${String(o.remarks)} ${String(remark)}`.slice(0, 99),
        remarkColor: color,
      }));
      setRemarkPopupAnchorEl(null);
    },
    [Memo]
  );

  // 选择是否显示border
  const handleSelectBorderLine = useCallback(() => {
    setForm((o) => ({
      ...o,
      borderLine: o.borderLine === 'show' ? 'hide' : 'show',
    }));
  }, []);

  const dialogTitle = useMemo(() => {
    if (Memo) {
      return 'Memo';
    } else if (NurseStation) {
      return 'Nurese Station';
    } else {
      return 'Cubicle';
    }
  }, [Memo, NurseStation]);

  const renderRemarkContent = () => {
    return (
      <Box display="flex" gridGap="1.25rem">
        <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <div className={classes.title}>
              Remarks
              <span className={classes.titleRemark}>{`(${Memo ? 1000 : 100} words limited)`} </span>
            </div>
            <Box display="flex" alignItems="center" gridGap="0.969rem">
              <ButtonBase className={classes.addRemarkBtn} onClick={handleRemarkPopup}>
                <AddIcon className={classes.addIcon} />
                Add Remarks
              </ButtonBase>
              <div className={classes.border} />
              <ButtonBase onClick={handleRemarkOpen}>
                <div className={classes.palette} />
                {remarkAnchorEl ? (
                  <DropUpIcon className={classes.dropDownIcon} />
                ) : (
                  <DropDownIcon className={classes.dropDownIcon} />
                )}
              </ButtonBase>
            </Box>
          </Box>
          {Memo ? (
            <RichText value={form.remarks} onChange={updateForm('remarks')} />
          ) : (
            <InputBase
              className={classes.remarkInput}
              placeholder="Add remarks here"
              inputProps={{
                maxLength: Memo ? NEW_BED_STATUS_LENGTH.MEMO_REMARKS : NEW_BED_STATUS_LENGTH.CUBICLE_REMARKS,
              }}
              value={form.remarks}
              onChange={updateForm('remarks')}
              multiline
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderCubicleContent = () => {
    return (
      <>
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <Box display="flex">
              <div className={classes.title}>Cubicle Status </div>
              <LongRemark
                content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
              />
            </Box>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Status"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
              value={form.status}
              freeSolo
              onChange={updateForm('status')}
              maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <Box display="flex">
              <div className={classes.title}>Cubicle Type </div>
              <LongRemark
                content="• For general cubicles, leave it blank.
• For adding a nurse station or other non-cubicle area, kindly select “Nurse Station/Misc.”.
• If there is no suitable type in the list, kindly input by free text
"
              />
            </Box>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Type"
              freeSolo
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleType}
              value={form.type}
              onChange={updateForm('type')}
              maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_TYPE}
            />
          </Box>
        </Box>
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <div className={classes.title}>Gender</div>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Gender"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.Gender}
              value={form.gender}
              onChange={updateForm('gender')}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <div className={classes.title}>Cubicle Colour </div>
            <AutoCompleteSelect
              type="color"
              width="100%"
              placeholder="Select Colour"
              itemList={cubicleColorList}
              value={form.color}
              onChange={updateForm('color')}
              colorIconBorder="solid 1px #e4e7eb"
            />
            <Box display="flex" alignItems="center" gridGap="0.75rem">
              <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
              <div className={classes.content}>Show border line</div>
            </Box>
          </Box>
        </Box>
        {renderRemarkContent()}
      </>
    );
  };

  const renderMemoContent = () => {
    return (
      <>
        <Box display="flex" gridGap="1.25rem">
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <Box display="flex">
              <div className={classes.title}> Status </div>
              <LongRemark
                content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
              />
            </Box>
            <AutoCompleteInput
              width="100%"
              placeholder="Select Status"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
              value={form.status}
              freeSolo
              onChange={updateForm('status')}
              maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
            />
          </Box>
          <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
            <div className={classes.title}> Colour </div>
            <AutoCompleteSelect
              type="color"
              width="100%"
              placeholder="Select Colour"
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
              value={form.color}
              onChange={updateForm('color')}
              colorIconBorder="solid 1px #e4e7eb"
            />
            <Box display="flex" alignItems="center" gridGap="0.75rem">
              <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
              <div className={classes.content}>Show border line</div>
            </Box>
          </Box>
        </Box>
        {renderRemarkContent()}
      </>
    );
  };
  const renderNurseStationContent = () => {
    return (
      <Box display="flex" gridGap="1.25rem">
        <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
          <div className={classes.title}>
            Name<span className={classes.star}>*</span>
          </div>
          <InputBase
            className={classes.input}
            placeholder="Name"
            inputProps={{ maxLength: NEW_BED_STATUS_LENGTH.CUBICLE_NAME }}
            value={form.name}
            onChange={updateForm('name')}
          />
        </Box>
        <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
          <div className={classes.title}> Colour </div>
          <AutoCompleteSelect
            type="color"
            width="100%"
            placeholder="Select Colour"
            itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
            value={form.color}
            onChange={updateForm('color')}
            colorIconBorder="solid 1px #e4e7eb"
          />
          <Box display="flex" alignItems="center" gridGap="0.75rem">
            <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
            <div className={classes.content}>Show border line</div>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (NurseStation) {
      return renderNurseStationContent();
    } else if (Memo) {
      return renderMemoContent();
    } else {
      return renderCubicleContent();
    }
  };

  return (
    <>
      <Dialog
        open={showPopup}
        width={Memo ? '75vw' : '50rem'}
        title={dialogTitle}
        dialogfootStyle={{ paddingTop: '1.5rem', borderTop: 'solid 1px #e4e7eb', marginTop: '1.875rem' }}
        handleClose={handleClose}
        haveFoot={false}
      >
        <Box display="flex" flexDirection="column" gridGap="1.25rem">
          {renderContent()}
        </Box>

        {/* Footer */}
        <Box display="flex" justifyContent="center" mt="3.25rem" gridGap="1.25rem">
          <StyleButton text="Cancel" width="12.5rem" onClick={handleClose} />
          <StyleButton onClick={handleConfirm} text="Update" type="fill" width="12.5rem" backgroundColor="#28d2ab" />
        </Box>
      </Dialog>

      {/* remark color popup */}
      <Popper
        className={classes.remarkColorPopup}
        open={Boolean(remarkAnchorEl)}
        anchorEl={remarkAnchorEl}
        placement="bottom-end"
        transition
      >
        <Box
          display="flex"
          flexDirection="column"
          width="10rem"
          border="solid 1px #e4e7eb"
          bgcolor="#fff"
          boxShadow="0 1px 6px 0 rgba(0, 0, 0, 0.12)"
          borderRadius="10px"
          padding="0.5rem 0"
        >
          {NEW_BED_STATUS_CUBICLE_ITEM_LIST.RemarkColor.map((item) => (
            <ButtonBase className={classes.colourItem} key={item.value} onClick={updateForm('remarkColor')(item)}>
              <div className={classes.palette} style={{ backgroundColor: item.color }} /> {item.text}
            </ButtonBase>
          ))}
        </Box>
      </Popper>

      <CubicleRemarksPopup
        anchorEl={remarkPopupAnchorEl}
        handleClose={handleCloseRemarkPopup}
        handleClick={handleSelectRemark}
      />
    </>
  );
};

export default LandingUpdateCubicleDialog;
